import React, { useState, useEffect, useRef } from 'react';
import { Grid, TextField, Typography, Box } from '@mui/material';
import { TextInputQuestion } from './AssessmentQuestionType';
import AssessmentStepControButtonGroup from './AssessmentStepControlButtonGroup';
import {
  AssessmentQuestionAction,
  AssessmentQuestionActionType,
} from './AssessmentQuestionAction';
import { isStringEmpty } from '../../utils/formValidation';
import APICaller, { MethodType } from '../common/APICaller';
import { useParams } from 'react-router-dom';
import FileDownload from '../common/FileDownload';
import { API_BASE_URL } from '../../utils/utils';

interface TextInputQuestionStepProps {
  data: TextInputQuestion;
  index: number;
  isLast: boolean;
  isFirst: boolean;
  goNext: () => void;
  goBack: () => void;
  handleSubmit: () => void;
  dispatch: React.Dispatch<AssessmentQuestionAction>;
}

const TextInputQuestionStep: React.FC<TextInputQuestionStepProps> = ({
  data,
  index,
  isLast,
  isFirst,
  goNext,
  goBack,
  handleSubmit,
  dispatch,
}) => {
  const [textInput, setTextInput] = useState(data.answer);
  const { assessmentId } = useParams();
  
  // Track last saved value to prevent duplicate saves
  const lastSavedRef = useRef<string>(data.answer);
  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const handleTextInputChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setTextInput(event.target.value);
  };

  // Autosave function
  const saveAnswer = (answer: string) => {
    // Skip if empty or unchanged
    if (isStringEmpty(answer) || answer === lastSavedRef.current) {
      return Promise.resolve();
    }

    const url = data.answerId
      ? `/application/assessments/answers/${data.answerId}/text/update`
      : `/application/assessments/questions/${data.id}/answer/text/create`;
    const method = data.answerId ? MethodType.PUT : MethodType.POST;
    const body = {
      answer: {
        text: answer,
      },
    };

    return APICaller(url, method, body)
      .then(res => {
        if (!res.errorMessage) {
          if (res.answerId) {
            dispatch({
              type: AssessmentQuestionActionType.UpdateTextField,
              payload: {
                question_index: index,
                answer: answer,
                answerId: res.answerId,
              },
            });
          } else {
            dispatch({
              type: AssessmentQuestionActionType.UpdateTextField,
              payload: {
                question_index: index,
                answer: answer,
              },
            });
          }
          lastSavedRef.current = answer;
          // Silent autosave - no notification
        }
      })
      .catch(e => {
        console.log(e);
      });
  };

  // Debounced autosave on text change
  useEffect(() => {
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }

    if (textInput !== lastSavedRef.current && !isStringEmpty(textInput)) {
      saveTimeoutRef.current = setTimeout(() => {
        saveAnswer(textInput);
      }, 1500); // 1.5 second delay
    }

    return () => {
      if (saveTimeoutRef.current) {
        clearTimeout(saveTimeoutRef.current);
      }
    };
  }, [textInput]);

  const handleNext = async () => {
    // Cancel pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Save immediately before navigating if there are unsaved changes
    if (textInput !== lastSavedRef.current && !isStringEmpty(textInput)) {
      await saveAnswer(textInput);
    }
    goNext();
  };

  const handleBack = async () => {
    // Cancel pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Save immediately before navigating if there are unsaved changes
    if (textInput !== lastSavedRef.current && !isStringEmpty(textInput)) {
      await saveAnswer(textInput);
    }
    goBack();
  };

  const handleSubmitWithSave = async () => {
    // Cancel pending debounced save
    if (saveTimeoutRef.current) {
      clearTimeout(saveTimeoutRef.current);
    }
    
    // Save immediately before submitting if there are unsaved changes
    if (textInput !== lastSavedRef.current && !isStringEmpty(textInput)) {
      await saveAnswer(textInput);
    }
    handleSubmit();
  };
  return (
    <>
      <Grid container direction="column">
        <Typography variant="h6">{data.description}</Typography>
        {data.hasQuestionFile && (
          <Box sx={{ mb: 1 }}>
            <FileDownload
              url={`/assessments/${assessmentId}/question/${data.id}/file/download`}
              displayedText="Download Question"
              width="25%"
            />
          </Box>
        )}
        <TextField
          variant="outlined"
          label="Your Answer"
          multiline
          minRows={4}
          value={textInput}
          onChange={handleTextInputChange}
          sx={{ width: '90%', textAlign: 'left' }}
        />
      </Grid>
      <AssessmentStepControButtonGroup
        isLast={isLast}
        isFirst={isFirst}
        handleNext={handleNext}
        handleBack={handleBack}
        handleSubmit={handleSubmitWithSave}
        showSaveBtn={false}
      />
    </>
  );
};

export default TextInputQuestionStep;
